/**
 * API keys for the REPSit.ai public API (Settings → Organization → API access).
 * Keys are minted and revoked by Cloud Functions (`hugoCreateApiKey`, `hugoRevokeApiKey`) so the
 * plaintext is shown once and only the hash is stored. Lists read `Hugo_ApiKeys` / `Hugo_ApiLog`
 * directly (rules: org admins of that organization).
 */
import { collection, getDocs, query, where, orderBy, limit as fsLimit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { db } from '../firebase';

const functions = getFunctions(app, 'us-central1');

export const API_SCOPES = Object.freeze([
  { key: 'organization:read', label: 'Organization structure', help: 'Sites, departments, Natural Work Teams, thresholds.' },
  { key: 'members:read', label: 'Members', help: 'Names, emails, roles and scopes of your members.' },
  { key: 'scores:read', label: 'OE scores & snapshots', help: 'REPS scores, element breakdown, warnings, history.' },
  { key: 'conditions:read', label: 'OE Conditions — read', help: 'Every logged condition and its review status.' },
  { key: 'conditions:write', label: 'OE Conditions — create', help: 'Log conditions from your own systems.' },
  { key: 'alerts:read', label: 'OE health alerts', help: 'Active risk alerts and recommendations.' },
  { key: 'surveys:read', label: 'Surveys', help: 'Survey list, status and response targets.' },
  { key: 'library:read', label: 'Learning Library', help: 'Industry case studies and your uploaded documents.' },
  { key: 'webhooks:manage', label: 'Webhooks', help: 'Register endpoints that receive events as they happen.' },
]);

export const READ_ONLY_SCOPES = API_SCOPES.map((s) => s.key).filter((k) => k.endsWith(':read'));

export async function listApiKeys(organizationId) {
  if (!organizationId) return [];
  const snap = await getDocs(query(collection(db, 'Hugo_ApiKeys'), where('organizationId', '==', organizationId)));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
}

export async function createApiKey({ organizationId, label, scopes, expiresInDays = null }) {
  const callable = httpsCallable(functions, 'hugoCreateApiKey');
  const res = await callable({ organizationId, label, scopes, expiresInDays });
  return res.data; // { keyId, key (ONCE), prefix, label, scopes, expiresAt }
}

export async function revokeApiKey(keyId) {
  const callable = httpsCallable(functions, 'hugoRevokeApiKey');
  const res = await callable({ keyId });
  return res.data;
}

export async function listApiLog(organizationId, { limit = 50 } = {}) {
  if (!organizationId) return [];
  const snap = await getDocs(query(collection(db, 'Hugo_ApiLog'), where('organizationId', '==', organizationId), orderBy('at', 'desc'), fsLimit(limit)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function listWebhooks(organizationId) {
  if (!organizationId) return [];
  const snap = await getDocs(query(collection(db, 'Hugo_ApiWebhooks'), where('organizationId', '==', organizationId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
